import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// onesignal — chargement conditionnel du SDK OneSignal Web (v16).
//
// L'intégration est pilotée depuis le SuperAdmin (partie API & IA) :
// elle n'est chargée QUE si elle est activée et qu'un App ID est configuré.
// OneSignal permet d'envoyer de vraies notifications push en arrière-plan
// (application fermée), en complément du système de notification interne.
// ─────────────────────────────────────────────────────────────

export interface OneSignalConfig {
  enabled: boolean;
  appId: string;
  safariWebId: string;
}

interface OneSignalSDK {
  init: (options: Record<string, unknown>) => Promise<void>;
  login: (externalId: string) => Promise<void>;
  logout: () => Promise<void>;
  Notifications: { requestPermission: () => Promise<void> };
  User?: { PushSubscription?: { optedIn?: boolean } };
}

declare global {
  interface Window {
    OneSignalDeferred?: Array<(os: OneSignalSDK) => void | Promise<void>>;
    OneSignal?: OneSignalSDK;
  }
}

const SCRIPT_ID = 'onesignal-sdk';
let currentAppId = '';

/** Lit la configuration OneSignal depuis le back-office (best-effort). */
export async function fetchOneSignalConfig(): Promise<OneSignalConfig | null> {
  try {
    const { data } = await supabase
      .from('zifek')
      .select('onesignal_enabled, onesignal_app_id, onesignal_safari_web_id')
      .limit(1)
      .maybeSingle();
    if (!data) return null;
    return {
      enabled: !!data.onesignal_enabled,
      appId: String(data.onesignal_app_id || '').trim(),
      safariWebId: String(data.onesignal_safari_web_id || '').trim(),
    };
  } catch {
    return null;
  }
}

function injectScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('no-document'));
      return;
    }
    if (document.getElementById(SCRIPT_ID)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('onesignal-load-failed'));
    document.head.appendChild(script);
  });
}

/**
 * Charge et initialise le SDK OneSignal pour cet App ID.
 * Ne fait rien si déjà initialisé pour le même App ID.
 */
export async function initOneSignal(appId: string, safariWebId?: string): Promise<boolean> {
  if (!appId) return false;
  if (typeof window === 'undefined') return false;
  if (currentAppId === appId && window.OneSignal) return true;

  try {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    await injectScript();
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.init({
          appId,
          safari_web_id: safariWebId || undefined,
          allowLocalhostAsSecureOrigin: true,
          notifyButton: { enable: false },
        });
      } catch {
        /* init best-effort */
      }
    });
    currentAppId = appId;
    return true;
  } catch {
    return false;
  }
}

/** Associe l'utilisateur connecté à OneSignal (external_id = id Zifek). */
export async function loginOneSignal(userId: number | string): Promise<void> {
  if (typeof window === 'undefined' || !currentAppId) return;
  try {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.login(String(userId));
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* ignore */
  }
}

/** Désassocie l'utilisateur (déconnexion). */
export async function logoutOneSignal(): Promise<void> {
  if (typeof window === 'undefined' || !currentAppId) return;
  try {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.logout();
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* ignore */
  }
}

/** Demande l'autorisation de notification via OneSignal (moteur actif). */
export async function requestOneSignalPermission(): Promise<void> {
  if (typeof window === 'undefined' || !currentAppId) return;
  try {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.Notifications.requestPermission();
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* ignore */
  }
}